import { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import { Pressable } from './Common/Pressable'
import type { Goal, SubTask, Task, TimelineItem } from '../types/timeline'
import { toDate } from '../utils/timeUtils'
import { useNavigation } from '../context/useNavigation'
import { useCanvasScale } from '../context/useCanvasScale'
import { useCanvasGestures } from '../hooks/useCanvasGestures'
import { useNls } from '../nls/nlsProvider'
import { getDashboardCanvasWelcomePhrases } from './DashboardNls'

const MotionPressable = motion.create(Pressable)

const ROW_HEIGHT = 132
const LABEL_HEIGHT = 44
const ROW_PADDING = 12
const springTransition = { type: 'spring', bounce: 0.2, duration: 0.6 } as const
const windowTransition = { type: 'spring', stiffness: 120, damping: 20 } as const
const DAY_MS = 24 * 60 * 60 * 1000
const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())
const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1)
const startOfYear = (date: Date) => new Date(date.getFullYear(), 0, 1)
const startOfWeek = (date: Date) => {
  const dayStart = startOfDay(date)
  const day = dayStart.getDay()
  const mondayOffset = day === 0 ? -6 : 1 - day
  return new Date(dayStart.getTime() + mondayOffset * DAY_MS)
}

type TimelineMarker = {
  id: string
  date: Date
  label: string
}

const getWeekNumber = (date: Date) => {
  const firstDay = new Date(date.getFullYear(), 0, 1)
  const days = Math.floor((startOfDay(date).getTime() - firstDay.getTime()) / DAY_MS)
  return Math.ceil((days + firstDay.getDay() + 1) / 7)
}

const getMarkerPosition = (date: Date, viewStartMs: number, viewDurationMs: number) =>
  ((date.getTime() - viewStartMs) / viewDurationMs) * 100

const buildTimelineMarkers = (
  viewStart: Date,
  viewEnd: Date,
  locale: string,
  formatWeekLabel: (week: number) => string,
): TimelineMarker[] => {
  const viewStartMs = viewStart.getTime()
  const viewEndMs = viewEnd.getTime()
  const visibleDays = (viewEndMs - viewStartMs) / DAY_MS
  const markers: TimelineMarker[] = []

  if (visibleDays < 14) {
    const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric' })
    let cursor = startOfDay(viewStart)
    while (cursor.getTime() <= viewEndMs) {
      markers.push({ id: `day-${cursor.getTime()}`, date: cursor, label: formatter.format(cursor) })
      cursor = new Date(cursor.getTime() + DAY_MS)
    }
    return markers
  }

  if (visibleDays <= 60) {
    let cursor = startOfWeek(viewStart)
    while (cursor.getTime() <= viewEndMs) {
      markers.push({
        id: `week-${cursor.getTime()}`,
        date: cursor,
        label: formatWeekLabel(getWeekNumber(cursor)),
      })
      cursor = new Date(cursor.getTime() + 7 * DAY_MS)
    }
    return markers
  }

  if (visibleDays <= 730) {
    const formatter = new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' })
    let cursor = startOfMonth(viewStart)
    while (cursor.getTime() <= viewEndMs) {
      markers.push({ id: `month-${cursor.getTime()}`, date: cursor, label: formatter.format(cursor) })
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1)
    }
    return markers
  }

  let cursor = startOfYear(viewStart)
  while (cursor.getTime() <= viewEndMs) {
    markers.push({ id: `year-${cursor.getTime()}`, date: cursor, label: String(cursor.getFullYear()) })
    cursor = new Date(cursor.getFullYear() + 1, 0, 1)
  }

  return markers
}

const getPosition = (item: TimelineItem, viewStartMs: number, viewDurationMs: number) => {
  const startMs = toDate(item.startDate).getTime()
  const endMs = toDate(item.endDate).getTime()

  return {
    left: `${((startMs - viewStartMs) / viewDurationMs) * 100}%`,
    width: `${(Math.max(DAY_MS, endMs - startMs) / viewDurationMs) * 100}%`,
  }
}

const getNestedPosition = (item: TimelineItem, parent: TimelineItem) => {
  const parentStartMs = toDate(parent.startDate).getTime()
  const parentEndMs = toDate(parent.endDate).getTime()
  const itemStartMs = toDate(item.startDate).getTime()
  const itemEndMs = toDate(item.endDate).getTime()
  const parentDurationMs = Math.max(DAY_MS, parentEndMs - parentStartMs)

  return {
    left: `${((itemStartMs - parentStartMs) / parentDurationMs) * 100}%`,
    width: `${(Math.max(DAY_MS, itemEndMs - itemStartMs) / parentDurationMs) * 100}%`,
  }
}

const isFocusedBranch = (goal: Goal, focusedItemId: string | null) => {
  if (!focusedItemId) return true
  if (goal.id === focusedItemId) return true
  return goal.tasks.some(
    (task) => task.id === focusedItemId || task.subTasks.some((subTask) => subTask.id === focusedItemId),
  )
}

const isDimmedTask = (task: Task, focusedItemId: string | null) => {
  if (!focusedItemId) return false
  if (task.id === focusedItemId) return false
  return !task.subTasks.some((subTask) => subTask.id === focusedItemId)
}

const getTaskLabel = (task: Task, locale: string) => {
  const start = new Intl.DateTimeFormat(locale, { month: 'short', year: '2-digit' }).format(
    new Date(task.startDate),
  )
  const end = new Intl.DateTimeFormat(locale, { month: 'short', year: '2-digit' }).format(
    new Date(task.endDate),
  )

  return `${task.title} / ${start} - ${end}`
}

export const DashboardCanvas = ({
  goals,
}: {
  goals: readonly Goal[]
}) => {
  const { locale, t } = useNls()
  const {
    currentLevel,
    activeGoalId,
    activeTaskId,
    activeSubTaskId,
    goToGoal,
    goToTask,
    goToSubTask,
  } = useNavigation()
  const {
    viewWindowStart,
    viewWindowEnd,
    focusedItemId,
    focusItem,
    zoomAt,
    panByRatio,
  } = useCanvasScale()
  const [welcomeIndex, setWelcomeIndex] = useState(0)
  const welcomePhrases = useMemo(() => getDashboardCanvasWelcomePhrases(t), [t])
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const viewStartMs = viewWindowStart.getTime()
  const viewEndMs = viewWindowEnd.getTime()
  const viewDurationMs = Math.max(DAY_MS, viewEndMs - viewStartMs)
  const markers = buildTimelineMarkers(viewWindowStart, viewWindowEnd, locale, (week) => t('NLSDashboardTimelineMarkerWeek', { week }))
  const activeGoal = goals.find((goal) => goal.id === activeGoalId)
  const activeTask = activeGoal?.tasks.find((task) => task.id === activeTaskId)
  const activeSubTask = activeTask?.subTasks.find((subTask) => subTask.id === activeSubTaskId)
  const visibleGoals = currentLevel === 'dashboard' ? goals : activeGoal ? [activeGoal] : goals

  useEffect(() => {
    if (goals.length > 0) return undefined

    const interval = window.setInterval(() => {
      setWelcomeIndex((currentIndex) => (currentIndex + 1) % welcomePhrases.length)
    }, 2200)

    return () => window.clearInterval(interval)
  }, [goals.length, welcomePhrases.length])

  const getAnchorRatio = (clientX: number) => {
    const bounds = viewportRef.current?.getBoundingClientRect()
    if (!bounds || bounds.width === 0) return 0.5
    return Math.min(1, Math.max(0, (clientX - bounds.left) / bounds.width))
  }

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return undefined

    const handleNativeWheel = (event: globalThis.WheelEvent) => {
      const shouldZoom = event.ctrlKey || event.metaKey || Math.abs(event.deltaY) > Math.abs(event.deltaX)
      const shouldPan = !event.ctrlKey && !event.metaKey && Math.abs(event.deltaX) > Math.abs(event.deltaY)

      if (!shouldZoom && !shouldPan) return

      event.preventDefault()
      event.stopPropagation()

      if (shouldPan) {
        panByRatio(event.deltaX / Math.max(1, viewport.clientWidth))
        return
      }

      const zoomIntensity = event.ctrlKey || event.metaKey ? 0.004 : 0.0018
      const zoomFactor = Math.exp(event.deltaY * zoomIntensity)
      zoomAt(getAnchorRatio(event.clientX), zoomFactor)
    }

    viewport.addEventListener('wheel', handleNativeWheel, { passive: false })
    return () => viewport.removeEventListener('wheel', handleNativeWheel)
  }, [panByRatio, zoomAt])

  const openRowForLevel = (goal: Goal, rowTask: Task | null | undefined, rowSubTask: SubTask | null | undefined) => {
    if (rowSubTask && rowTask && currentLevel === 'subtask') {
      openSubTask(goal, rowTask, rowSubTask)
      return
    }

    if (rowTask && (currentLevel === 'task' || currentLevel === 'subtask')) {
      openTask(goal, rowTask)
      return
    }

    openGoal(goal)
  }

  const openBlockForLevel = (goal: Goal, block: Task | SubTask, containingTask?: Task) => {
    const isTaskBlock = 'subTasks' in block

    if (currentLevel === 'dashboard') {
      openGoal(goal)
      return
    }

    if (currentLevel === 'goal') {
      const nextTask = isTaskBlock ? block : containingTask
      if (nextTask) openTask(goal, nextTask)
      return
    }

    if (currentLevel === 'task') {
      if (!isTaskBlock && containingTask) openSubTask(goal, containingTask, block)
      return
    }

    if (currentLevel === 'subtask' && !isTaskBlock && containingTask) {
      openSubTask(goal, containingTask, block)
    }
  }

  // Touch gestures: single-finger horizontal pan with momentum, pinch-zoom,
  // and double-tap to step zoom in. The hook owns the pointer-event lifecycle.
  useCanvasGestures(
    viewportRef,
    {
      onPan: (dxPx) => {
        const width = viewportRef.current?.clientWidth ?? 1
        if (!width) return
        // Horizontal canvas only consumes x; pan by negative dx (drag right
        // shows earlier content, which is a backward pan ratio).
        panByRatio(-dxPx / width)
      },
      onPinch: (anchorXPx, _anchorYPx, scaleFactor) => {
        const width = viewportRef.current?.clientWidth ?? 1
        const ratio = Math.min(1, Math.max(0, anchorXPx / Math.max(1, width)))
        // The hook reports scaleFactor where >1 = zoom-in; zoomAt expects a
        // smaller factor to zoom in (it multiplies the visible window), so
        // invert.
        zoomAt(ratio, 1 / Math.max(0.001, scaleFactor))
      },
      onDoubleTap: (anchorXPx) => {
        const width = viewportRef.current?.clientWidth ?? 1
        const ratio = Math.min(1, Math.max(0, anchorXPx / Math.max(1, width)))
        zoomAt(ratio, 0.6) // step in
      },
    },
    { enableSingleFingerPan: true },
  )

  const openGoal = (goal: Goal) => {
    focusItem(goal.id, goal)
    goToGoal(goal.id)
  }

  const openTask = (goal: Goal, task: Task) => {
    focusItem(task.id, task)
    goToTask(goal.id, task.id)
  }

  const openSubTask = (goal: Goal, task: Task, subTask: SubTask) => {
    focusItem(subTask.id, subTask)
    goToSubTask(goal.id, task.id, subTask.id)
  }

  if (goals.length === 0) {
    return (
      <motion.section
        className="emptyWelcomeRegion relative w-full max-w-none overflow-hidden rounded-lg"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -18 }}
        transition={{ duration: 0.22 }}
      >
        <div className="emptyWelcomeContent">
          <span className="emptyWelcomeMark" aria-hidden="true">
            <AutoAwesomeRoundedIcon fontSize="inherit" />
          </span>
          <span className="pointer-events-none absolute inset-0 opacity-35 [background-image:linear-gradient(color-mix(in_srgb,var(--text-strong)_3.5%,transparent)_1px,transparent_1px),linear-gradient(90deg,color-mix(in_srgb,var(--text-strong)_3.5%,transparent)_1px,transparent_1px)] [background-size:44px_44px,44px_44px]" />
          <motion.span
            className="pointer-events-none absolute bottom-0 left-1/2 h-48 w-[44rem] -translate-x-1/2 rounded-[100%] bg-[var(--surface-muted)] blur-3xl"
            animate={{ opacity: [0.55, 0.9, 0.55], scale: [1, 1.08, 1] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.span
            className="pointer-events-none absolute left-[18%] top-[30%] h-1 w-32 rounded-md bg-[var(--chart-1)]/70"
            animate={{ x: [0, 52, 0], opacity: [0.25, 0.75, 0.25] }}
            transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.span
            className="pointer-events-none absolute right-[18%] top-[66%] h-1 w-40 rounded-md bg-[var(--chart-4)]/60"
            animate={{ x: [0, -60, 0], opacity: [0.2, 0.68, 0.2] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />

          <div className="emptyWelcomeText">
            <span className="emptyWelcomeEyebrow">{t('NLSDashboardEmptyAiMagicReady')}</span>
            <span className="emptyWelcomeHeadlineFrame relative block w-full overflow-hidden perspective-[900px]">
              <AnimatePresence mode="wait">
                <motion.span
                  key={welcomePhrases[welcomeIndex]}
                  className="emptyWelcomeHeadline"
                  initial={{ opacity: 0, y: 64, rotateX: -76, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, rotateX: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -64, rotateX: 76, scale: 0.96 }}
                  transition={{ type: 'spring', bounce: 0.18, duration: 0.8 }}
                >
                  {welcomePhrases[welcomeIndex]}
                </motion.span>
              </AnimatePresence>
            </span>
            <p className="emptyWelcomeCopy">
              {t('NLSDashboardEmptyAiMagicCopy')}
            </p>
          </div>
        </div>
      </motion.section>
    )
  }

  return (
    <motion.section
      className="themeSurface w-full max-w-none overflow-hidden rounded-lg"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -18 }}
      transition={{ duration: 0.22 }}
    >
      <div
        ref={viewportRef}
        className="dashboardZoomViewport overflow-hidden overscroll-x-contain touch-none"
        style={{ touchAction: 'none' }}
      >
        <div className="pb-4 pt-5">
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 right-0">
              {markers.map((marker) => (
                <motion.span
                  key={marker.id}
                  className="dashboardGridLine absolute inset-y-0 border-l"
                  animate={{ left: `${getMarkerPosition(marker.date, viewStartMs, viewDurationMs)}%` }}
                  transition={windowTransition}
                />
              ))}
            </div>

            <div className="relative z-[1] grid gap-3 pb-12">
              <div className="dashboardAxis relative z-20 h-10 overflow-hidden border-y text-xs font-bold backdrop-blur">
                {markers.map((marker) => (
                  <motion.span
                    key={marker.id}
                    className="absolute px-2 py-3"
                    data-orbit-target="marker"
                    data-marker-id={marker.id}
                    data-orbit-title={marker.label}
                    animate={{ left: `${getMarkerPosition(marker.date, viewStartMs, viewDurationMs)}%` }}
                    transition={windowTransition}
                  >
                    {marker.label}
                  </motion.span>
                ))}
              </div>

              {visibleGoals.map((goal) => {
                const rowTask =
                  currentLevel === 'task' || currentLevel === 'subtask'
                    ? activeTask
                    : null
                const rowSubTask = currentLevel === 'subtask' ? activeSubTask : null
                const rowItem = rowSubTask ?? rowTask ?? goal
                const rowColor =
                  rowSubTask?.color ?? rowTask?.color ?? goal.baseColor
                const rowTitle = rowItem.title
                const rowMeta =
                  rowSubTask
                    ? t('NLSDashboardCanvasSelectedSubtask')
                    : rowTask
                      ? t('NLSDashboardCanvasSubtasksAcrossTask', { count: rowTask.subTasks.length })
                      : t('NLSDashboardCanvasTasksAcrossGoal', { count: goal.tasks.length })
                const rowBlocks: Array<Task | SubTask> = rowSubTask
                  ? [rowSubTask]
                  : rowTask
                    ? rowTask.subTasks.length > 0
                      ? rowTask.subTasks
                      : [rowTask]
                    : goal.tasks

                return (
                  <motion.div
                    key={`${currentLevel}-${rowItem.id}`}
                    transition={springTransition}
                    role="button"
                    tabIndex={0}
                    className="dashboardGoalRow group relative w-full rounded-lg border border-transparent bg-transparent px-3 pb-3 pt-2 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    data-orbit-target="goal-row"
                    data-goal-id={goal.id}
                    data-orbit-title={goal.title}
                    animate={{
                      height: currentLevel === 'dashboard' && !isFocusedBranch(goal, focusedItemId) ? 54 : ROW_HEIGHT,
                      opacity: currentLevel === 'dashboard' && !isFocusedBranch(goal, focusedItemId) ? 0.28 : 1,
                    }}
                    onClick={() => openRowForLevel(goal, rowTask, rowSubTask)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        openRowForLevel(goal, rowTask, rowSubTask)
                      }
                    }}
                  >
                    <MotionPressable
                      type="button"
                      className="dashboardGoalLabel sticky left-3 top-0 z-20 flex min-h-9 w-max max-w-[calc(100vw-48px)] items-center gap-3 rounded-md border px-3 py-2 text-left shadow-sm backdrop-blur transition-colors focus:outline-none"
                      animate={{
                        opacity: currentLevel === 'dashboard' ? 1 : 0.72,
                        scale: currentLevel === 'dashboard' ? 1 : 0.96,
                        y: currentLevel === 'dashboard' ? 0 : -2,
                      }}
                      transition={windowTransition}
                      onClick={(event) => {
                        event.stopPropagation()
                        openRowForLevel(goal, rowTask, rowSubTask)
                      }}
                    >
                    <span className="h-3.5 w-3.5 flex-none rounded-md" style={{ backgroundColor: rowColor }} />
                    <span className="themeStrong whitespace-nowrap text-sm font-bold leading-tight sm:text-base">
                      {rowTitle}
                    </span>
                    <span className="themeMuted whitespace-nowrap text-xs leading-tight">
                      {rowMeta}
                    </span>
                    </MotionPressable>

                  <motion.div
                    className="dashboardTaskTrack absolute inset-x-3 rounded-md border"
                    animate={{ top: LABEL_HEIGHT, bottom: ROW_PADDING }}
                    transition={windowTransition}
                  >
                    {rowBlocks.map((block) => {
                      const blockPosition = getPosition(block, viewStartMs, viewDurationMs)
                      const isTaskBlock = 'subTasks' in block
                      const hasSubTasks = isTaskBlock && block.subTasks.length > 0 && !rowTask

                      return (
                        <motion.div
                          key={block.id}
                          layout
                          layoutId={`${isTaskBlock ? 'task' : 'subtask'}-${block.id}`}
                          transition={windowTransition}
                          role="button"
                          tabIndex={0}
                          className="absolute top-3 min-w-10 overflow-hidden rounded-md p-0 shadow-sm ring-1 ring-[var(--border)] transition-[box-shadow,transform] hover:z-10 hover:scale-[1.01] hover:shadow-md focus-visible:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
                          data-orbit-target="task-card"
                          data-task-id={block.id}
                          data-orbit-title={block.title}
                          animate={{
                            ...blockPosition,
                            height: 'calc(100% - 24px)',
                            opacity: isTaskBlock && isDimmedTask(block, focusedItemId) ? 0.24 : 1,
                            scale: currentLevel === 'dashboard' ? 0.985 : 1,
                          }}
                          style={{
                            backgroundColor: isTaskBlock ? block.color : block.color,
                          }}
                          onClick={(event) => {
                            event.stopPropagation()
                            openBlockForLevel(goal, block, rowTask ?? (isTaskBlock ? block : undefined))
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              event.stopPropagation()
                              openBlockForLevel(goal, block, rowTask ?? (isTaskBlock ? block : undefined))
                            }
                          }}
                          aria-label={isTaskBlock ? getTaskLabel(block, locale) : block.title}
                          title={isTaskBlock ? getTaskLabel(block, locale) : block.title}
                        >
                          {hasSubTasks ? (
                            <span className="grid h-full grid-rows-2">
                              <span className="flex min-w-0 items-center bg-transparent px-2 text-[11px] font-bold leading-none text-[var(--text-strong)]">
                                <span className="truncate">{block.title}</span>
                              </span>
                              <span className="relative overflow-hidden border-t border-[var(--border-subtle)] bg-[var(--surface-muted)]/20">
                                {block.subTasks.map((subTask) => (
                                  <motion.span
                                    key={subTask.id}
                                    className="absolute inset-y-0 min-w-2 rounded-none border-r border-[var(--border-subtle)] last:border-r-0"
                                    data-orbit-target="task-card"
                                    data-task-id={subTask.id}
                                    data-orbit-title={subTask.title}
                                    animate={{
                                      ...getNestedPosition(subTask, block),
                                      opacity:
                                        focusedItemId && focusedItemId !== subTask.id && block.id !== focusedItemId
                                          ? 0.36
                                          : 1,
                                    }}
                                    transition={windowTransition}
                                    style={{
                                      backgroundColor: subTask.color,
                                      borderRadius: 0,
                                    }}
                                    onClick={(event) => {
                                      event.stopPropagation()
                                      openBlockForLevel(goal, subTask, block)
                                    }}
                                    title={subTask.title}
                                  />
                                ))}
                              </span>
                            </span>
                          ) : (
                            <span className="flex h-full min-w-0 items-center px-2 text-[11px] font-bold leading-none text-[var(--text-strong)]">
                              <span className="truncate">{block.title}</span>
                            </span>
                          )}
                        </motion.div>
                      )
                    })}
                  </motion.div>

                    </motion.div>
                )
              })}
            </div>

          </div>
        </div>
      </div>
    </motion.section>
  )
}
